import { buildUrl } from '@amazeelabs/react-framework-bridge';
import {
  buildHtml,
  buildLink,
} from '@amazeelabs/react-framework-bridge/gatsby';
import { LocaleCode, useTranslations } from '@nuklear9/i18n';
import { ComponentProps } from '@nuklear9/meta';
import { StandardLayout } from '@nuklear9/ui';
import { WindowLocation } from '@reach/router';
import merge from 'lodash/merge';

import imageUrlFallback from '../images/default.jpeg';
import { LocationState, PageContext, PartialDeep } from '../types';
import { kernwissenTagsPaths } from './kernwissen-tags-paths';
import { myTopicsPaths } from './my-topics-paths';
import { searchPaths } from './search-paths';
import { useFooterMenu } from './use-footer-menu';
import { useMainMenu } from './use-main-menu';
import { useMetaMenu } from './use-meta-menu';
import { useSiteMetadata } from './use-site-metadata';

/**
 * Map the current page context to properties for the standard layout.
 *
 * @param pageContext
 *   The current page context.
 * @param location
 * @param additionalProps
 *   Additional properties to pass to the layout
 */
export const useLayoutProps = (
  pageContext: PageContext,
  location: WindowLocation<LocationState>,
  additionalProps?: PartialDeep<ComponentProps<typeof StandardLayout>>,
): ComponentProps<typeof StandardLayout> => {
  const siteMetadata = useSiteMetadata();
  const mainMenu = useMainMenu(pageContext.locale);
  const metaMenu = useMetaMenu(pageContext.locale);
  const footerMenu = useFooterMenu(pageContext.locale);
  const t = useTranslations();
  const myTopicsPath = myTopicsPaths.filter(
    (p) => p.locale === pageContext.locale,
  )[0];
  const kernwissenTagsPath = kernwissenTagsPaths.filter(
    (p) => p.locale === pageContext.locale,
  )[0];
  const searchPath = searchPaths.filter(
    (p) => p.locale === pageContext.locale,
  )[0];
  return merge<
    ComponentProps<typeof StandardLayout>,
    PartialDeep<ComponentProps<typeof StandardLayout>> | undefined
  >(
    {
      buildHtml,
      preFooter: {
        NewsletterLink: buildLink({ segments: ['/', pageContext.locale] }),
        MembershipLink: buildLink({ segments: ['/', pageContext.locale] }),
      },
      header: {
        HomeLink: buildLink({ segments: ['/', pageContext.locale] }),
        KnowledgeLink: buildLink({ segments: ['/', pageContext.locale, kernwissenTagsPath.path] }),
        FilterLink: buildLink({
          segments: ['/', pageContext.locale, myTopicsPath.path],
        }),
        SearchLink: buildLink({
          segments: ['/', pageContext.locale, searchPath.path],
        }),
        mainMenu,
        metaMenu,
        localizations: pageContext.localizations.reduce(
          (localizations, { path, langcode }) => {
            localizations[langcode as LocaleCode] = buildLink({ href: path });

            return localizations;
          },
          {} as ComponentProps<
            typeof StandardLayout
          >['header']['localizations'],
        ),
      },
      footerTags: [],
      footerMenu,
      // messages: location.state?.messages,
      seo: {
        siteName: siteMetadata.site!.siteMetadata.title,
        countryCode: siteMetadata.site!.siteMetadata.countryCode,
        url: buildUrl([
          siteMetadata.site!.siteMetadata.baseUrl,
          location.pathname,
        ]),
        imageUrl: buildUrl([
          siteMetadata.site!.siteMetadata.baseUrl,
          imageUrlFallback,
        ]),
        imageAlt: t('meta.imageAlt'),
        localizations: pageContext.localizations.map((item) => ({
          langcode: item.langcode,
          url: buildUrl([siteMetadata.site!.siteMetadata.baseUrl, item.path]),
        })),
      },
    },
    additionalProps,
  );
};
